// "Filtrar por titular" (Main artboard, nav rail top): scopes every screen to one AnmEntity via the `titular` search
// param, so it survives navigation, reloads and shared links.
import type { AnmEntityOption } from '@req/contracts';
import * as Select from '@radix-ui/react-select';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

const ALL = 'all';

export function AnmEntityFilter({ entities }: { entities: readonly AnmEntityOption[] }) {
  const { t } = useTranslation();
  const { titular } = useSearch({ strict: false });
  const navigate = useNavigate();
  const processes = entities.reduce((s, e) => s + e.processCount, 0);
  const value = titular && entities.some((e) => e.id === titular) ? titular : ALL;

  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-semibold tracking-wide text-faint uppercase">{t('filter.label')}</span>
      <Select.Root
        value={value}
        onValueChange={(v) => void navigate({ to: '.', search: (prev) => ({ ...prev, titular: v === ALL ? undefined : v }) })}
      >
        <Select.Trigger
          aria-label={t('filter.label')}
          className="flex items-center justify-between rounded-button border border-line bg-card px-3 py-2 text-left text-sm text-ink shadow-sm"
        >
          <Select.Value />
          <Select.Icon className="text-faint">▾</Select.Icon>
        </Select.Trigger>
        <Select.Portal>
          <Select.Content position="popper" sideOffset={4} className="z-50 min-w-56 rounded-button border border-line bg-card p-1 shadow-md">
            <Select.Viewport>
              <Select.Item value={ALL} className="cursor-pointer rounded-pill px-3 py-2 text-sm outline-none data-[highlighted]:bg-wash">
                <Select.ItemText>{t('filter.all')}</Select.ItemText>
              </Select.Item>
              {entities.map((e) => (
                <Select.Item key={e.id} value={e.id} className="cursor-pointer rounded-pill px-3 py-2 text-sm outline-none data-[highlighted]:bg-wash">
                  <Select.ItemText>{e.label}</Select.ItemText>
                  <span className="ml-2 text-xs text-faint tabular">{e.processCount}</span>
                </Select.Item>
              ))}
            </Select.Viewport>
          </Select.Content>
        </Select.Portal>
      </Select.Root>
      <span className="text-xs text-faint tabular">{t('filter.summary', { entities: entities.length, processes })}</span>
    </div>
  );
}
